import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Search, ScrollText, FileDown, Crown, UserCheck } from "lucide-react";
import { Modal, Input, Table, Pagination, Badge, Tooltip } from "./UI";
import { apiFetch, useApp } from "../../context/AppContext";

// ─── Head / SPOC / Delegate audit helpers ──────────────────────────────────

const HEAD_ACTION_LABELS = {
  set_head: "Marked as Head Department",
  unset_head: "Head Department removed",
  set_spoc: "Assigned as SPOC",
  unset_spoc: "SPOC removed",
  assign_delegate: "Delegate access granted",
  remove_delegate: "Delegate access revoked",
};

function headActionBadge(action) {
  if (action === "assign_delegate" || action === "set_spoc" || action === "set_head") {
    return <Badge variant="success">{HEAD_ACTION_LABELS[action] || action}</Badge>;
  }
  if (action === "remove_delegate" || action === "unset_spoc" || action === "unset_head") {
    return <Badge variant="danger">{HEAD_ACTION_LABELS[action] || action}</Badge>;
  }
  return <Badge variant="default">{HEAD_ACTION_LABELS[action] || action}</Badge>;
}

function formatDateOnly(value) {
  if (!value) return null;
  try {
    return new Date(value + "T00:00:00").toLocaleDateString();
  } catch {
    return value;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function csvCell(value) {
  if (value === null || value === undefined) return "";
  const str = String(value);
  if (str.includes(",") || str.includes('"') || str.includes("\n")) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function buildCsv(rows) {
  const headers = ["When", "Action", "Module", "Method", "Status", "Duration (ms)"];
  const lines = [headers.map(csvCell).join(",")];
  for (const row of rows) {
    lines.push(
      [
        row.created_at
          ? new Date(row.created_at).toLocaleString(undefined, {
              dateStyle: "medium",
              timeStyle: "short",
            })
          : "",
        row.action,
        row.resource,
        row.method,
        row.status_code,
        row.duration_ms ?? "",
      ]
        .map(csvCell)
        .join(",")
    );
  }
  return lines.join("\r\n");
}

function downloadFile(content, filename, mimeType) {
  const blob = new Blob(["\uFEFF" + content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return value;
  }
}

function methodBadge(method) {
  const variants = {
    GET: "primary",
    POST: "success",
    PUT: "warning",
    PATCH: "warning",
    DELETE: "danger",
  };
  return <Badge variant={variants[method] || "default"}>{method}</Badge>;
}

function statusBadge(code) {
  if (code >= 200 && code < 400) return <Badge variant="success">{code}</Badge>;
  if (code >= 400 && code < 500) return <Badge variant="warning">{code}</Badge>;
  return <Badge variant="danger">{code}</Badge>;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Modal showing a single user's activity trail (filtered slice of the
 * organisation-wide audit log). Opened from the Users list via an
 * "Activity" button on each user row.
 */
export default function UserActivityLogModal({ user, onClose }) {
  const { showNotification } = useApp();
  const [activeTab, setActiveTab] = useState("general"); // "general" | "head"

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const pageSize = 10;

  // Head / SPOC / Delegate audit trail (not paginated server-side — capped at 100)
  const [headLogs, setHeadLogs] = useState([]);
  const [headLoading, setHeadLoading] = useState(false);

  const fetchHeadLogs = useCallback(async () => {
    if (!user?.id) return false;
    setHeadLoading(true);
    try {
      const res = await apiFetch(`/head-department/audit?user_id=${user.id}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("Failed to fetch Head/Delegate history");
      const data = await res.json();
      setHeadLogs(Array.isArray(data) ? data : []);
      return true;
    } catch (e) {
      showNotification(e.message || "Failed to fetch Head/Delegate history", "error");
      return false;
    } finally {
      setHeadLoading(false);
    }
  }, [user?.id, showNotification]);

  useEffect(() => {
    if (user?.id && activeTab === "head") {
      fetchHeadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeTab]);

  const fetchLogs = useCallback(
    async (targetPage = 1, searchTerm = "") => {
      if (!user?.id) return false;
      setLoading(true);
      try {
        const query = new URLSearchParams({
          page: String(targetPage),
          page_size: String(pageSize),
          user_id: String(user.id),
        });
        if (searchTerm) query.append("search", searchTerm);
        const res = await apiFetch(`/tenant/audit-logs?${query.toString()}`, {
          cache: "no-store",
        });
        if (!res.ok) throw new Error("Failed to fetch activity logs");
        const data = await res.json();
        setLogs(data.items || []);
        setTotal(data.total || 0);
        setPage(data.page || 1);
        setPages(data.pages || 1);
        return true;
      } catch (e) {
        showNotification(e.message || "Failed to fetch activity logs", "error");
        return false;
      } finally {
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user?.id, showNotification]
  );

  const skipNextSearchFetch = useRef(true);

  useEffect(() => {
    if (user?.id) {
      skipNextSearchFetch.current = true; // the fetch below covers the reset search too
      setSearch("");
      setActiveTab("general");
      fetchLogs(1, "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    if (skipNextSearchFetch.current) {
      // Skip the run that fires right after the reset-on-open effect above.
      skipNextSearchFetch.current = false;
      return;
    }
    const t = setTimeout(() => fetchLogs(1, search), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Explicit manual refresh — re-fetches from the server and confirms via toast on success.
  const handleRefreshGeneral = useCallback(async () => {
    const ok = await fetchLogs(page, search);
    if (ok) showNotification("Activity log refreshed", "success");
  }, [fetchLogs, page, search, showNotification]);

  const handleRefreshHead = useCallback(async () => {
    const ok = await fetchHeadLogs();
    if (ok) showNotification("Head & Delegate log refreshed", "success");
  }, [fetchHeadLogs, showNotification]);

  const handleExportExcel = async () => {
    if (!user?.id) return;
    setExporting(true);
    try {
      const query = new URLSearchParams({
        page: "1",
        page_size: "10000",
        user_id: String(user.id),
      });
      if (search) query.append("search", search);
      const res = await apiFetch(`/tenant/audit-logs?${query.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch logs for export");
      const data = await res.json();
      const allItems = data.items || [];
      if (allItems.length === 0) {
        showNotification("No activity to export", "warning");
        return;
      }
      const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const safeName = (user.full_name || "user").replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      downloadFile(
        buildCsv(allItems),
        `activity-log-${safeName}-${timestamp}.csv`,
        "text/csv;charset=utf-8"
      );
      showNotification(`Exported ${allItems.length} log entries`, "success");
    } catch (e) {
      showNotification(e.message || "Export failed", "error");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: "_sr",
      label: "S.No",
      render: (_val, _row, index) => (
        <span className="text-slate-500 font-medium text-xs">
          {(page - 1) * pageSize + index + 1}
        </span>
      ),
    },
    {
      key: "created_at",
      label: "When",
      render: (val) => (
        <span className="text-slate-800 whitespace-nowrap text-xs">{formatWhen(val)}</span>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (val, row) => (
        <div className="min-w-[160px]">
          <div className="text-slate-900 text-sm">{val}</div>
          <div className="text-[11px] text-slate-500 capitalize">
            {row.resource?.replace(/[-_]/g, " ")}
          </div>
        </div>
      ),
    },
    { key: "method", label: "Method", render: (val) => methodBadge(val) },
    { key: "status_code", label: "Status", render: (val) => statusBadge(val) },
    {
      key: "duration_ms",
      label: "Time",
      render: (val) => (
        <span className="text-xs text-slate-600 whitespace-nowrap">
          {val != null ? `${val} ms` : "—"}
        </span>
      ),
    },
  ];

  const headColumns = [
    {
      key: "_sr",
      label: "S.No",
      render: (_val, _row, index) => (
        <span className="text-slate-500 font-medium text-xs">{index + 1}</span>
      ),
    },
    {
      key: "created_at",
      label: "When",
      render: (val) => (
        <span className="text-slate-800 whitespace-nowrap text-xs">{formatWhen(val)}</span>
      ),
    },
    {
      key: "action",
      label: "Action",
      render: (val) => headActionBadge(val),
    },
    {
      key: "department_name",
      label: "Department",
      render: (val) => (
        <span className="text-slate-700 text-sm">{val || "—"}</span>
      ),
    },
    {
      key: "start_date",
      label: "Period",
      render: (_val, row) =>
        row.start_date || row.end_date ? (
          <span className="text-xs text-slate-600 whitespace-nowrap">
            {formatDateOnly(row.start_date) || "—"} → {formatDateOnly(row.end_date) || "—"}
          </span>
        ) : (
          <span className="text-xs text-slate-400">—</span>
        ),
    },
    {
      key: "actor_name",
      label: "By",
      render: (_val, row) => (
        <div className="min-w-[120px]">
          <div className="text-slate-800 text-xs font-medium">{row.actor_name || "—"}</div>
          <div className="text-[11px] text-slate-500 truncate">{row.actor_email || ""}</div>
        </div>
      ),
    },
    {
      key: "note",
      label: "Note",
      className: "max-w-[260px]",
      render: (val) => (
        <span className="text-xs text-slate-500 whitespace-normal break-words">
          {val || "—"}
        </span>
      ),
    },
  ];

  const tabButtonClass = (tab) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
      activeTab === tab
        ? "bg-amber-600 text-white shadow-sm"
        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
    }`;

  return (
    <Modal
      open={!!user}
      onClose={onClose}
      size="xl"
      title={
        <div className="flex items-center gap-2">
          <ScrollText size={16} className="text-amber-600" />
          <span>Activity Log{user ? ` — ${user.full_name}` : ""}</span>
        </div>
      }
    >
      {user && (
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100">
            <div className="w-9 h-9 rounded-full bg-slate-600 flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
              {(user.full_name || "?")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">
                {user.full_name}
              </p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
            {activeTab === "general" && total > 0 && (
              <span className="ml-auto flex-shrink-0 text-xs text-slate-500 whitespace-nowrap">
                {total} event{total !== 1 ? "s" : ""}
              </span>
            )}
            {activeTab === "head" && headLogs.length > 0 && (
              <span className="ml-auto flex-shrink-0 text-xs text-slate-500 whitespace-nowrap">
                {headLogs.length} event{headLogs.length !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {/* Tabs */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setActiveTab("general")}
              className={tabButtonClass("general")}
            >
              <ScrollText size={12} /> All Activity
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("head")}
              className={tabButtonClass("head")}
            >
              <Crown size={12} /> Head &amp; Delegate
            </button>
          </div>

          {activeTab === "general" ? (
            <>
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Search this user's actions…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  leftElement={<Search size={14} className="text-slate-400" />}
                  className="flex-1"
                />
                <Tooltip content={exporting ? "Exporting…" : "Export to Excel"}>
                  <button
                    type="button"
                    onClick={handleExportExcel}
                    disabled={exporting || loading}
                    className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                      exporting || loading
                        ? "bg-emerald-50 text-emerald-300 border-emerald-100 cursor-not-allowed"
                        : "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-700 hover:border-emerald-300 active:scale-95"
                    }`}
                  >
                    {exporting ? (
                      <RefreshCw size={15} className="animate-spin" />
                    ) : (
                      <FileDown size={15} />
                    )}
                  </button>
                </Tooltip>
                <Tooltip content="Refresh">
                  <button
                    type="button"
                    onClick={handleRefreshGeneral}
                    disabled={loading}
                    aria-label="Refresh activity log"
                    className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                      loading
                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800 hover:border-slate-300 active:scale-95"
                    }`}
                  >
                    <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
                  </button>
                </Tooltip>
              </div>

              {loading && logs.length === 0 ? (
                <div className="flex items-center justify-center py-14 gap-2 text-slate-500 text-sm">
                  <RefreshCw size={14} className="animate-spin" /> Loading activity…
                </div>
              ) : (
                <>
                  <div className="relative">
                    {loading && (
                      <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl bg-white/70 backdrop-blur-[1px] text-sm text-slate-600">
                        <RefreshCw size={16} className="animate-spin text-amber-600" />
                        Refreshing…
                      </div>
                    )}
                    <div className="max-h-[380px] overflow-y-auto">
                      <Table
                        columns={columns}
                        data={logs}
                        emptyMessage="No activity recorded for this user yet."
                      />
                    </div>
                  </div>
                  <Pagination
                    page={page}
                    pages={pages}
                    total={total}
                    pageSize={pageSize}
                    onPageChange={(p) => fetchLogs(p, search)}
                    itemLabel="events"
                  />
                </>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-500 flex items-center gap-1.5">
                  <UserCheck size={12} className="text-emerald-600" />
                  Head Department, SPOC, and Delegate assignments involving this user — as
                  subject or as the assigning SPOC Head.
                </p>
                <Tooltip content="Refresh">
                  <button
                    type="button"
                    onClick={handleRefreshHead}
                    disabled={headLoading}
                    aria-label="Refresh Head & Delegate log"
                    className={`flex-shrink-0 w-9 h-9 rounded-lg border flex items-center justify-center transition-all ${
                      headLoading
                        ? "bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed"
                        : "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100 hover:text-slate-800 hover:border-slate-300 active:scale-95"
                    }`}
                  >
                    <RefreshCw size={15} className={headLoading ? "animate-spin" : ""} />
                  </button>
                </Tooltip>
              </div>

              {headLoading && headLogs.length === 0 ? (
                <div className="flex items-center justify-center py-14 gap-2 text-slate-500 text-sm">
                  <RefreshCw size={14} className="animate-spin" /> Loading Head &amp; Delegate
                  history…
                </div>
              ) : (
                <div className="relative">
                  {headLoading && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl bg-white/70 backdrop-blur-[1px] text-sm text-slate-600">
                      <RefreshCw size={16} className="animate-spin text-amber-600" />
                      Refreshing…
                    </div>
                  )}
                  <div className="max-h-[420px] overflow-y-auto">
                    <Table
                      columns={headColumns}
                      data={headLogs}
                      emptyMessage="No Head Department, SPOC, or Delegate activity for this user yet."
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
